import { Dialog } from '@progress/kendo-react-dialogs';
import { Checkbox, Input } from '@progress/kendo-react-inputs';
import React from 'react';
import { useEvent } from '../../contexts/event-bus';
import { useShortcut } from '../../hooks/use-shortcut';
import MarkerText from '../inputs/MarkerText';
import './command-center.css';
import { Command } from './types';
import { useCommands } from './use-commands';
import { useSearchCommands } from './use-search-commands';
import { useWeatherCommands } from './use-weather-commands';

function CommandShortcut({ command }: { command: Command }) {
    useShortcut(command.shortcut, () => command.run());
    return null;
}

function filterCommands(commands: Command[], query: string): Command[] {
    const q = query.trim().toLowerCase();
    if (!q) {
        return commands;
    }
    return commands.filter((c) => {
        const haystack = [c.title, ...(c.keywords ?? [])].join(' ').toLowerCase();
        return q.split(/\s+/).every((term) => haystack.includes(term));
    });
}

export default function CommandCenter() {
    const commands = useCommands();
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState('');
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const listRef = React.useRef<HTMLDivElement>(null);

    const filtered = React.useMemo(() => filterCommands(commands, query), [commands, query]);
    const searchCommands = useSearchCommands(query);
    const weatherCommands = useWeatherCommands(query);
    const results = React.useMemo(() => [...filtered, ...searchCommands, ...weatherCommands], [filtered, searchCommands, weatherCommands]);
    const shortcutCommands = React.useMemo(() => commands.filter((c) => c.shortcut), [commands]);

    const openPalette = React.useCallback(() => {
        setQuery('');
        setSelectedIndex(0);
        setOpen(true);
    }, []);

    // Ctrl/Cmd+K (convention) and Alt+K both toggle the palette.
    useShortcut('mod+k', () => setOpen((prev) => !prev));
    useShortcut('alt+k', () => setOpen((prev) => !prev));
    useEvent('open-command-center', openPalette);

    React.useEffect(() => {
        setSelectedIndex(0);
    }, [query]);

    React.useEffect(() => {
        listRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex, results]);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        const onPointerDown = (e: MouseEvent) => {
            if (contentRef.current && !contentRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    const runCommand = React.useCallback((command: Command) => {
        setOpen(false);
        command.run();
    }, []);

    const onInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const command = results[selectedIndex];
            if (command) {
                runCommand(command);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
        }
    };

    return (
        <>
            {shortcutCommands.map((c) => (
                <CommandShortcut key={c.id} command={c} />
            ))}

            {open && (
                <Dialog className="command-center-dialog" onClose={() => setOpen(false)}>
                    <div id="command-center" className="command-center flight-details-scrollbars" ref={contentRef}>
                        <Input
                            autoFocus
                            className="command-center-input"
                            data-allow-shortcuts="true"
                            placeholder="Search anything..."
                            value={query}
                            onChange={(e) => setQuery(String(e.value ?? ''))}
                            onKeyDown={onInputKeyDown}
                        />
                        <div className="command-center-list" ref={listRef}>
                            {results.length === 0 && (
                                <div className="command-center-empty secondary-text-color px-3">No matching commands</div>
                            )}
                            {results.map((command, index) => {
                                const showHeader = command.group && command.group !== results[index - 1]?.group;
                                return (
                                    <React.Fragment key={command.id}>
                                        {showHeader && (
                                            <div className="command-center-group secondary-text-color px-3">{command.group}</div>
                                        )}
                                        <div
                                            data-selected={index === selectedIndex}
                                            className={`command-center-item clickable px-3 d-flex align-items-center justify-content-between ${
                                                index === selectedIndex ? 'command-center-selected-bg' : ''
                                            }`}
                                            onMouseEnter={() => setSelectedIndex(index)}
                                            onClick={() => runCommand(command)}>
                                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                                {command.checked !== undefined && (
                                                    <Checkbox checked={command.checked} readOnly style={{ pointerEvents: 'none' }} />
                                                )}
                                                <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                                                    <MarkerText highlight={query}>{command.title}</MarkerText>
                                                    {command.subtitle && (
                                                        <span className="command-center-subtitle secondary-text-color text-truncate">
                                                            {command.subtitle}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {command.shortcutLabel && (
                                                <span className="command-center-shortcut fe-badge-outline">{command.shortcutLabel}</span>
                                            )}
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                </Dialog>
            )}
        </>
    );
}
